import { useSearchParams } from 'react-router-dom';
import { Sparkles, Newspaper, Coffee } from 'lucide-react';
import { PageHeader, Tabs, type TabItem } from '../../ui';
import { DigestContent } from '../Digest/DigestPage';
import { CatchMeUpContent } from '../CatchMeUp/CatchMeUpPage';
import { resolveRecapTab, TAB_PARAM } from '../../app/consolidatedNav';

/**
 * Recap — the merged AI summarizer surface (UX audit Top-10 #4). Combines the
 * two near-identical "pick a window → Bedrock summary → sources" screens into
 * one page with `Digest` / `Catch Me Up` tabs, composing the existing content
 * components so the intelligence hooks (`useDigest`, `useCatchMeUp`) are not
 * duplicated:
 *
 *  - **Digest**: a skimmable report for a reporting window ({@link
 *    DigestContent}).
 *  - **Catch Me Up**: a get-back-up-to-speed briefing since a chosen date
 *    ({@link CatchMeUpContent}).
 *
 * The active tab is reflected in `?tab=` so the redirects from `/digest` and
 * `/catch-me-up` (and any bookmarks) land on the right tab. Chat and Ask Vector
 * stay separate surfaces, per the audit.
 */
const TAB_ITEMS: TabItem[] = [
  { id: 'digest', label: 'Digest', icon: <Newspaper className="h-4 w-4" aria-hidden="true" /> },
  {
    id: 'catch-me-up',
    label: 'Catch Me Up',
    icon: <Coffee className="h-4 w-4" aria-hidden="true" />,
  },
];

export function RecapPage() {
  const [params, setParams] = useSearchParams();
  const tab = resolveRecapTab(params.get(TAB_PARAM));

  const setTab = (id: string) => {
    const next = new URLSearchParams(params);
    // `digest` is the default view, so keep the URL clean by omitting the param.
    if (id === 'catch-me-up') next.set(TAB_PARAM, 'catch-me-up');
    else next.delete(TAB_PARAM);
    setParams(next, { replace: true });
  };

  return (
    <div>
      <PageHeader
        icon={<Sparkles className="h-5 w-5" />}
        title="Recap"
        description="Grounded AI summaries of your team's work — a windowed digest or a catch-up briefing since a date you choose."
      />

      <div className="mb-6">
        <Tabs items={TAB_ITEMS} value={tab} onChange={setTab} />
      </div>

      {tab === 'digest' ? <DigestContent /> : <CatchMeUpContent />}
    </div>
  );
}

export default RecapPage;
